import { KeyIcon, WifiIcon, XMarkIcon, ArrowsPointingOutIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { eventDetailsBareUrl } from "../utils/links";

export default function QRCodeComponent({ hideQR, currentEvent }) {
  const [qrSize, setQrSize] = useState(324);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        wakeLock = await navigator.wakeLock.request('screen');
      } catch (err) {
        console.error(`${err.name}, ${err.message}`);
      }
    };

    const releaseWakeLock = () => {
      if (wakeLock) {
        wakeLock.release();
        wakeLock = null;
      }
    };

    // Request wake lock when component mounts
    if ('wakeLock' in navigator) {
      requestWakeLock();
    }

    const calculateSize = () => {
      // Calculate size based on viewport, accounting for padding/margins
      // Use 80% of the smaller dimension, capped at 300px max
      const padding = 64; // Approximate padding/margins around QR code
      const maxSize = 400;
      const availableWidth = window.innerWidth - padding * 2;
      const availableHeight = window.innerHeight - padding * 4; // More vertical padding for text/logo
      const size = Math.min(maxSize, availableWidth * 0.9, availableHeight * 0.9);
      setQrSize(Math.max(100, size)); // Ensure a minimum size
    };

    calculateSize(); // Initial calculation
    window.addEventListener('resize', calculateSize);

    // Cleanup listener and release wake lock on component unmount
    return () => {
      window.removeEventListener('resize', calculateSize);
      releaseWakeLock();
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const enterFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  };

  // Scheme-less, as it always was on admin.gistirl.com (GIS-188: the member-app
  // origin now comes from utils/links instead of being guessed from the hostname,
  // which pointed the code at the panel itself on any non-admin.* host).
  const autoJoinParam = currentEvent?.groupCount === 1 ? "?autoJoin=true" : "";
  const qrCodeUrl = currentEvent
    ? `${eventDetailsBareUrl(currentEvent.id)}${autoJoinParam}`
    : "";

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[60]">
      <div className="relative w-full h-full bg-primary p-4 flex flex-col items-center justify-center">
        <button
          onClick={() => { if (document.fullscreenElement) document.exitFullscreen(); hideQR(); }}
          className="absolute top-4 right-4 p-2 text-white md:opacity-50 md:hover:opacity-100"
          aria-label="Close QR Code"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
        {!isFullscreen && (
          <button
            onClick={enterFullscreen}
            className="absolute top-4 left-4 p-2 text-white"
            aria-label="Enter Fullscreen"
          >
            <ArrowsPointingOutIcon className="h-6 w-6" />
          </button>
        )}
        {/* Main layout: QR left on widescreen, text right; stacked on mobile */}
        <div className="flex flex-col lg:flex-row items-center justify-center lg:space-x-8">
          {/* QR Code */}
          <div className="flex flex-col items-center text-center mb-6 lg:mb-0">
            <h1 className="text-sm font-light pb-1 text-white">Scan to Join</h1>
            {qrCodeUrl && (
              <QRCode
                value={qrCodeUrl}
                size={qrSize} // Dynamic size
                bgColor="#F09E7B" // Background color
                fgColor="#FFFFFF" // QR code color
                level="H" // Error correction level: L, M, Q, H
              />
            )}
          </div>
          {/* Text content */}
          <div className="flex flex-col items-center text-center text-white">
            <img
              className="h-24 w-auto mb-2"
              src="/gist_logo.jpg"
              alt="gist logo"
            />
            <h3 className="w-full flex items-center justify-center text-sm font-light text-white mt-2 mb-1">
              Welcome to
            </h3>
            <h2 className="text-2xl lg:text-3xl font-bold mb-6 lg:mb-8 text-white text-center">
              {currentEvent.name}
            </h2>
            {/* Conditionally render WiFi info */}
            {currentEvent?.wifi?.name && (
              <div className="flex flex-row items-start justify-center space-x-8 lg:space-x-12 mt-4">
                {/* WiFi Name Section */}
                <div className="flex flex-col items-center">
                  <h3 className="flex items-center justify-center text-sm font-light text-white mb-1">
                    <WifiIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                  </h3>
                  <p className="text-md font-medium mt-1 break-words max-w-xs">
                    {currentEvent.wifi.name}
                  </p>
                </div>
                {/* WiFi Password Section */}
                <div className="flex flex-col items-center">
                  <h3 className="flex items-center justify-center text-sm font-light text-white mb-1">
                    <KeyIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                  </h3>
                  <p className="text-md font-medium mt-1 break-all max-w-xs">
                    {currentEvent.wifi.password}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
